import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Clock, ArrowRight } from 'lucide-react';
import '../components/BlogPostContent.css';

const API_BASE_URL = 'https://smartsolutionslk.com/api/api.php';
const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

const BlogPostPage = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/1200x800/1f2937/FFFFFF?text=No+Image';
        if (url.startsWith('data:image')) return url;
        if (url.startsWith('http')) return url;
        return `${IMAGE_BASE_URL}${url}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    // Rough word-count based read time
    const readTime = (content) => {
        if (!content) return null;
        const words = content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
        return Math.max(1, Math.round(words / 200));
    };

    useEffect(() => {
        const fetchPost = async () => {
            setIsLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}?resource=blogs`);
                const allPosts = await res.json();
                const found = allPosts.find(p => p.slug === slug);
                setPost(found || null);
                // Pick up to 3 other posts as "related"
                setRelatedPosts(allPosts.filter(p => p.slug !== slug).slice(0, 3));
            } catch (err) {
                console.error('Failed to fetch blog post:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPost();
        window.scrollTo(0, 0);
    }, [slug]);

    // ── Loading state ────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-black">
                {/* Hero skeleton */}
                <div className="relative pt-36 pb-16 px-6 bg-slate-50 dark:bg-gray-950 animate-pulse">
                    <div className="container mx-auto max-w-4xl">
                        <div className="h-4 w-28 bg-slate-200 dark:bg-gray-800 rounded mb-8" />
                        <div className="h-14 w-3/4 bg-slate-200 dark:bg-gray-800 rounded mb-4" />
                        <div className="h-14 w-1/2 bg-slate-200 dark:bg-gray-800 rounded mb-8" />
                        <div className="flex gap-4">
                            <div className="h-4 w-24 bg-slate-200 dark:bg-gray-800 rounded" />
                            <div className="h-4 w-24 bg-slate-200 dark:bg-gray-800 rounded" />
                        </div>
                    </div>
                </div>
                <div className="container mx-auto max-w-4xl px-6 py-12 animate-pulse space-y-4">
                    <div className="h-72 bg-slate-100 dark:bg-gray-900 rounded-2xl" />
                    <div className="h-4 w-full bg-slate-100 dark:bg-gray-900 rounded" />
                    <div className="h-4 w-5/6 bg-slate-100 dark:bg-gray-900 rounded" />
                    <div className="h-4 w-4/6 bg-slate-100 dark:bg-gray-900 rounded" />
                </div>
            </div>
        );
    }

    // ── Not found state ──────────────────────────────────────────────────────
    if (!post) {
        return (
            <div className="min-h-screen bg-white dark:bg-black flex flex-col items-center justify-center text-center px-6">
                <div className="text-6xl mb-6">📭</div>
                <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-3">
                    Post <span className="text-cyan-400">Not Found</span>
                </h1>
                <p className="text-slate-500 dark:text-gray-400 mb-8 max-w-md">
                    Sorry, we couldn't find the article you were looking for.
                </p>
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-500 text-black font-semibold hover:bg-cyan-400 transition-colors duration-200"
                >
                    <ArrowLeft size={18} />
                    Back to All Posts
                </Link>
            </div>
        );
    }

    const mins = readTime(post.content);

    return (
        <div className="min-h-screen bg-white dark:bg-black text-slate-900 dark:text-white">

            {/* ── Hero Banner ──────────────────────────────────────────────── */}
            <div className="relative pt-36 pb-16 px-6 overflow-hidden bg-slate-50 dark:bg-gray-950">
                {/* Glow decorations */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-32 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-20 right-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto max-w-4xl relative z-10">
                    {/* Back link */}
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-cyan-500 transition-colors mb-8 group"
                    >
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-200" />
                        Back to All Posts
                    </Link>

                    {/* Title — split first word white, rest cyan */}
                    <motion.h1
                        className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
                    >
                        {(() => {
                            const words = post.title.split(' ');
                            const mid = Math.ceil(words.length / 2);
                            return (
                                <>
                                    <span className="text-slate-900 dark:text-white">
                                        {words.slice(0, mid).join(' ')}{' '}
                                    </span>
                                    <span className="text-cyan-400">
                                        {words.slice(mid).join(' ')}
                                    </span>
                                </>
                            );
                        })()}
                    </motion.h1>

                    {/* Meta row */}
                    <motion.div
                        className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-gray-400"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {post.author && (
                            <span className="inline-flex items-center gap-1.5">
                                <User size={14} className="text-cyan-500" />
                                {post.author}
                            </span>
                        )}
                        {post.date && (
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar size={14} className="text-cyan-500" />
                                {formatDate(post.date)}
                            </span>
                        )}
                        {mins && (
                            <span className="inline-flex items-center gap-1.5">
                                <Clock size={14} className="text-cyan-500" />
                                {mins} min read
                            </span>
                        )}
                    </motion.div>

                    {/* Excerpt */}
                    {post.excerpt && (
                        <motion.p
                            className="mt-6 text-lg text-slate-500 dark:text-gray-400 italic leading-relaxed max-w-2xl border-l-4 border-cyan-500 pl-5"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.28 }}
                        >
                            {post.excerpt}
                        </motion.p>
                    )}
                </div>
            </div>

            {/* ── Cover Image ─────────────────────────────────────────────── */}
            <motion.div
                className="container mx-auto max-w-4xl px-6 -mt-2 pt-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
            >
                <div className="w-full rounded-2xl overflow-hidden shadow-2xl shadow-black/20 border border-slate-100 dark:border-gray-800">
                    <img
                        src={getImageSrc(post.image_url || post.image)}
                        alt={post.title}
                        className="w-full max-h-[480px] object-cover"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                        }}
                    />
                </div>
            </motion.div>

            {/* ── Article Body ─────────────────────────────────────────────── */}
            <motion.div
                className="container mx-auto max-w-4xl px-6 py-14"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
            >
                <div
                    className="blog-post-content prose prose-lg dark:prose-invert max-w-none
                               prose-p:text-slate-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
                               prose-h1:text-cyan-500 dark:prose-h1:text-cyan-400
                               prose-h2:text-cyan-500 dark:prose-h2:text-cyan-400
                               prose-h3:text-cyan-500 dark:prose-h3:text-cyan-400
                               prose-a:text-cyan-500 dark:prose-a:text-cyan-400 prose-a:no-underline hover:prose-a:underline
                               prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 prose-blockquote:pl-5 prose-blockquote:italic
                               prose-code:bg-slate-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:rounded
                               prose-pre:bg-slate-100 dark:prose-pre:bg-gray-800 prose-pre:rounded-xl prose-pre:shadow
                               prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8
                               prose-hr:border-slate-200 dark:prose-hr:border-gray-700 prose-hr:my-10
                               [&_p:empty]:hidden"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* ── Bottom divider ── */}
                <div className="mt-16 pt-10 border-t border-slate-200 dark:border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <p className="text-xs text-slate-400 dark:text-gray-500 uppercase tracking-widest mb-1">Written by</p>
                        <p className="font-semibold text-slate-900 dark:text-white">{post.author || 'Smart Solutions Team'}</p>
                    </div>
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-cyan-500 text-cyan-500 font-semibold text-sm hover:bg-cyan-500 hover:text-black transition-all duration-300 group"
                    >
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-200" />
                        All Articles
                    </Link>
                </div>
            </motion.div>

            {/* ── Related Posts ────────────────────────────────────────────── */}
            {relatedPosts.length > 0 && (
                <div className="bg-slate-50 dark:bg-gray-950 py-16 px-6">
                    <div className="container mx-auto max-w-7xl">
                        <h2 className="text-3xl font-extrabold mb-10">
                            <span className="text-slate-900 dark:text-white">More </span>
                            <span className="text-cyan-400">Articles</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {relatedPosts.map((rp, i) => (
                                <motion.div
                                    key={rp.id || i}
                                    className="group flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 border border-slate-100 dark:border-gray-800 hover:border-cyan-500/40"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.1 }}
                                    whileHover={{ y: -6 }}
                                >
                                    <Link to={`/blog/${rp.slug}`} className="block">
                                        <div className="relative w-full overflow-hidden" style={{ paddingTop: '56.25%' }}>
                                            <img
                                                src={getImageSrc(rp.image_url || rp.image)}
                                                alt={rp.title}
                                                className="absolute top-0 left-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-br from-slate-800/70 via-slate-900/60 to-cyan-900/70 group-hover:opacity-0 transition-opacity duration-500" />
                                        </div>
                                    </Link>
                                    <div className="p-6 flex flex-col flex-grow">
                                        <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-gray-500 mb-3">
                                            {rp.date && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={11} />
                                                    {formatDate(rp.date)}
                                                </span>
                                            )}
                                        </div>
                                        <Link to={`/blog/${rp.slug}`}>
                                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors leading-snug">
                                                {rp.title}
                                            </h3>
                                        </Link>
                                        {rp.excerpt && (
                                            <p className="text-slate-500 dark:text-gray-400 text-sm line-clamp-2 flex-grow">
                                                {rp.excerpt}
                                            </p>
                                        )}
                                        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-gray-800">
                                            <Link
                                                to={`/blog/${rp.slug}`}
                                                className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-500 dark:text-cyan-400 hover:text-cyan-600 transition-colors group/link"
                                            >
                                                Read Article
                                                <ArrowRight size={13} className="group-hover/link:translate-x-1 transition-transform duration-200" />
                                            </Link>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogPostPage;
